import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as CheckDefIcon } from "assets/images/icons/checkbox_default.svg";
import { ReactComponent as CheckClearIcon } from "assets/images/icons/checkbox_clear.svg";
import { ReactComponent as CheckFillIcon } from "assets/images/icons/checkbox_fiilled.svg";
import { ReactComponent as AddIcon } from "assets/images/icons/btn_add.svg";
import { ReactComponent as UploadIcon } from "assets/images/icons/icn_btn_export_pressed.svg";
import { ReactComponent as BinIcon } from "assets/images/icons/icn_btn_bin.svg";

import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import zIndex from "@mui/material/styles/zIndex";

import { OutlineButton } from "components/buttons/OutlineButton";
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import { SolidButton } from "components/buttons/SolidButton";

import AddUserAccountModal from "./AddUserAccountModal";
import UploadView from "./UploadView";
import UserAccountTableRow from "./TableRow";

const viewStateEnum = {
  MAIN: "MAIN",
  ADD: "ADD",
  UPLOAD: "UPLOAD",
  IMPORT: "IMPORT",
};

const MainView = ({
  getViewTitle,
  form,
  formValue,
  closeModal,
  handleMainView,
  isEditMode,
  showEditGeneral = true,
}) => {
  const theme = useTheme();
  const viewRef = useRef(null);
  const [isDirtyName, setIsDirtyName] = useState(false);
  const [viewState, setViewState] = useState(viewStateEnum.MAIN);
  const [selectedIndexes, setSelectedIndexes] = useState([]);

  const isNameValid = isDirtyName
    ? formValue.name.trim().length > 0
    : true;
  const viewWidth = viewRef && viewRef.current ? viewRef.current.offsetWidth : 0;
  let viewHeight = viewRef && viewRef.current ? viewRef.current.offsetHeight : 0;
  let marginTop = 0;
  if (viewRef && viewRef.current) {
    viewHeight = viewRef.current.offsetHeight;
    marginTop = viewHeight / 6;
  }

  const labelSx = {
    width: "150px",
    color: theme.palette.primary.greyPrimary,
    fontSize: theme.typography.customFontSize[2],
    fontWeight: theme.typography.customFontWeight[0],
  };
  const textfieldSx = (isValid = true) => ({
    width: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.customBorderRadius[1],
      height: "31px",
      backgroundColor: theme.palette.primary.white,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: isValid
          ? theme.palette.primary.borderGreyPrimary
          : theme.palette.primary.redPrimary,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: isValid
          ? theme.palette.primary.mainHover
          : theme.palette.primary.redPrimary,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: isValid
          ? theme.palette.primary.main
          : theme.palette.primary.redPrimary,
      },
    },
  });
  const getChildViewTitle = (targetViewState) => {
    switch (targetViewState) {
      case viewStateEnum.ADD:
        return "Add User Account";
      case viewStateEnum.UPLOAD:
        return "Add User Account by Upload";
      case viewStateEnum.IMPORT:
        return "Import User Account List";
      default:
        return getViewTitle();
    }
  };
  const closeChildView = () => {
    setViewState(viewStateEnum.MAIN);
  };

  return (
    <Box ref={viewRef} sx={{ minWidth: "760px", padding: 0, position: "relative" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[3],
            paddingLeft: "20px",
          }}
        >
          {getViewTitle()}
        </Typography>
        <Button
          onClick={closeModal}
          sx={{
            backgroundColor: theme.palette.primary.transparent,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: theme.palette.primary.transparent,
            },
          }}
        >
          <CloseIcon />
        </Button>
      </Box>

      <Box sx={{ paddingLeft: "26px", paddingRight: "26px" }}>
        <Box sx={{ marginLeft: "20px", marginTop: "20px", display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={labelSx}>User Profile Name</Box>
          <TextField
            inputProps={{ minLength: 1 }}
            sx={textfieldSx(isNameValid)}
            value={formValue.name}
            onChange={(e) => {
              setIsDirtyName(true);
              handleMainView.changeFormValue("name", e.target.value);
            }}
          />
        </Box>

        <Box sx={{ marginLeft: "20px", marginTop: "20px", display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={labelSx}>
            User Accounts: {formValue.userAccounts.length}
          </Box>
          <Box display="flex" gap="10px" justifyContent="flex-end" sx={{ flexGrow: 1 }}>
            <OutlineRectangleButton
              startIcon={<AddIcon />}
              height="32px"
              onClick={() => setViewState(viewStateEnum.ADD)}
            >
              Add
            </OutlineRectangleButton>
            <OutlineRectangleButton
              startIcon={<UploadIcon />}
              height="32px"
              onClick={() => setViewState(viewStateEnum.UPLOAD)}
            >
              Upload
            </OutlineRectangleButton>
            {selectedIndexes.length > 0 && (
              <OutlineRectangleButton
                startIcon={<BinIcon />}
                height="32px"
                onClick={() => {
                  handleMainView.deleteSelectedUserAccounts(selectedIndexes);
                  setSelectedIndexes([]);
                }}
              >
                Delete<Box sx={form.getNumberStyle()}>{selectedIndexes.length}</Box>
              </OutlineRectangleButton>
            )}
          </Box>
        </Box>

        <Box sx={{ marginTop: "10px", padding: "10px", maxHeight: "360px", overflowY: "auto" }}>
          <TableContainer
            component={Paper}
            sx={{ "&.MuiPaper-root": { boxShadow: "none", border: "none" } }}
            style={{ height: "300px", overflowY: "auto" }}
          >
            <Table
              sx={{
                borderCollapse: "collapse",
                "& td, & th": { border: "none" },
                "& tbody tr:nth-of-type(odd)": { backgroundColor: "rgb(246, 246, 246)" },
                "& tbody tr:nth-of-type(even)": { backgroundColor: theme.palette.primary.white },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "48px" }}>
                    <Checkbox
                      icon={<CheckDefIcon style={{ width: "25px", height: "25px" }} />}
                      checkedIcon={<CheckFillIcon style={{ width: "25px", height: "25px" }} />}
                      indeterminateIcon={<CheckClearIcon style={{ width: "25px", height: "25px" }} />}
                      indeterminate={
                        selectedIndexes.length > 0 &&
                        selectedIndexes.length < formValue.userAccounts.length
                      }
                      checked={
                        formValue.userAccounts.length > 0 &&
                        selectedIndexes.length === formValue.userAccounts.length
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIndexes(formValue.userAccounts.map((_, index) => index));
                        } else {
                          setSelectedIndexes([]);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Password</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description (Optional)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formValue.userAccounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: "center", padding: "20px" }}>
                      No entry in the table
                    </TableCell>
                  </TableRow>
                ) : (
                  formValue.userAccounts.map((item, index) => (
                    <UserAccountTableRow
                      key={index}
                      account={item}
                      index={index}
                      isSelected={selectedIndexes.includes(index)}
                      onSelect={(i) =>
                        setSelectedIndexes((prev) =>
                          prev.includes(i)
                            ? prev.filter((idx) => idx !== i)
                            : [...prev, i]
                        )
                      }
                      onChange={handleMainView.changeUserAccountItemValue}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      <Box sx={{ margin: "24px 0 16px auto", display: "flex", justifyContent: "center" }}>
        <OutlineButton
          onClick={closeModal}
          sx={{ marginRight: "20px" }}
          width="100px"
          height="32px"
        >
          Cancel
        </OutlineButton>
        <SolidButton
          onClick={handleMainView.handleSave}
          width="100px"
          height="32px"
          disabled={!handleMainView.checkFormValid() || (isEditMode && !showEditGeneral)}
        >
          {isEditMode ? "Apply" : "Create"}
        </SolidButton>
      </Box>

      {viewState !== viewStateEnum.MAIN && (
        <Box
          style={{
            ...form.getMaskStyle(),
            width: viewWidth + 40 + "px",
            height: viewHeight + 60 + "px",
            transform: "translateY(-20px) translateX(-20px)",
          }}
        />
      )}

      {viewState === viewStateEnum.ADD && (
        <Box
          style={{
            ...form.getInnerViewStyle(zIndex),
            width: viewWidth + 40 + "px",
            transform: `translate(-20px, ${marginTop}px)`,
          }}
        >
          <Box style={form.getInnerContainerStyle()}>
            <AddUserAccountModal
              open={viewState === viewStateEnum.ADD}
              onClose={closeChildView}
              onApply={handleMainView.addUserAccounts}
              form={form}
            />
          </Box>
        </Box>
      )}

      {viewState === viewStateEnum.UPLOAD && (
        <Box
          style={{
            ...form.getInnerViewStyle(zIndex),
            width: viewWidth + 40 + "px",
            transform: `translateX(-20px)`,
          }}
        >
          <Box style={form.getInnerContainerStyle()}>
            <UploadView
              viewStateEnum={viewStateEnum}
              getViewTitle={getChildViewTitle}
              form={form}
              handleUploadView={{
                closeView: closeChildView,
                updateUserAccountItemsList: handleMainView.importUserAccounts,
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(MainView);
